import React from "react";

/**
 * Hero silhouettes with animation.
 * - The silhouettes image is centered as one graphic (the source PNG has
 *   overlapping profiles, so they cannot be cleanly split without visual
 *   seams — the row moves as one gentle nod).
 * - Sparkles halo above each of the 7 heads with independent tempos.
 */

const HEADS = [
  { x: 12, y: 22, delayBase: 0.0 },
  { x: 31, y: 15, delayBase: 0.6 },
  { x: 44, y: 17, delayBase: 1.2 },
  { x: 56, y: 13, delayBase: 0.3 },
  { x: 68, y: 15, delayBase: 0.9 },
  { x: 80, y: 13, delayBase: 1.5 },
  { x: 92, y: 18, delayBase: 0.4 },
];

const SPARKLE_COLOR = "#7A5A2E";
const SPARKLE_GLOW = "rgba(122, 90, 46, 0.75)";

const Sparkle = ({ size, delay, dur, radius }) => (
  <span
    className="absolute pointer-events-none"
    style={{
      left: 0,
      top: 0,
      width: 0,
      height: 0,
      animation: `hero-sparkle-orbit ${dur}s linear ${delay}s infinite`,
      transformOrigin: `${radius}px 0`,
    }}
    aria-hidden="true"
  >
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      style={{
        position: "absolute",
        left: -size / 2,
        top: -size / 2,
        color: SPARKLE_COLOR,
        animation: `hero-sparkle-twinkle ${Math.max(1.6, dur / 2.4)}s ease-in-out ${delay}s infinite`,
        filter: `drop-shadow(0 0 6px ${SPARKLE_GLOW})`,
      }}
    >
      <path d="M12 2 L13.5 9 L20 10 L13.5 11 L12 18 L10.5 11 L4 10 L10.5 9 Z" fill="currentColor" />
    </svg>
  </span>
);

const HeroSilhouettes = () => {
  return (
    <>
      <style>{`
        @keyframes hero-sil-nod {
          0%, 100% { transform: rotate(-1.5deg); }
          50%      { transform: rotate(1.5deg); }
        }
        @keyframes hero-sil-nod-container {
          0%, 100% { transform: translateX(-50%) rotate(-1.5deg); }
          50%      { transform: translateX(-50%) rotate(1.5deg); }
        }
        @keyframes hero-sparkle-orbit {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes hero-sparkle-twinkle {
          0%, 100% { opacity: 0.55; transform: scale(0.75); }
          50%      { opacity: 1;    transform: scale(1.25); }
        }
      `}</style>

      {/* Silhouettes graphic — gently nodding as a group (source has overlapping profiles) */}
      <div
        className="pointer-events-none absolute left-1/2 bottom-0 select-none"
        style={{
          width: "min(1300px, 92%)",
          aspectRatio: "1312 / 833",
          transform: "translateX(-50%)",
          transformOrigin: "center bottom",
          animation: "hero-sil-nod-container 6s ease-in-out infinite",
        }}
        aria-hidden="true"
        data-testid="home-hero-bg-silhouettes"
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: "url('/assets/silhouettes.png')",
            backgroundSize: "100% 100%",
            backgroundRepeat: "no-repeat",
            opacity: 0.62,
            mixBlendMode: "multiply",
          }}
        />
        {/* Sparkles positioned relative to head anchor points in the image */}
        <div className="absolute inset-0" data-testid="home-hero-sparkles">
          {HEADS.map((h, i) => (
            <div
              key={i}
              className="absolute"
              style={{ left: `${h.x}%`, top: `${h.y}%`, width: 0, height: 0 }}
            >
              <Sparkle size={14} delay={h.delayBase}       dur={5}   radius={26} />
              <Sparkle size={11} delay={h.delayBase + 1.3} dur={6.2} radius={32} />
              <Sparkle size={9}  delay={h.delayBase + 2.6} dur={7.4} radius={22} />
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default HeroSilhouettes;
